import { Link, useForm } from "@inertiajs/react";
import { UserPlus, Trash2 } from "lucide-react";
import Steps, { Step } from "@/components/help/Steps";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";

function AddTeacherInline() {
  const { data, setData } = useForm({ name: "Mr. Patel", email: "mr.patel@lincoln.ca" });
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Teachers in this classroom</p>
      <ul className="space-y-1.5">
        <li className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-2 text-sm">
          <div>
            <p className="font-medium text-foreground">Ms. Lee</p>
            <p className="text-xs text-muted-foreground">ms.lee@lincoln.ca</p>
          </div>
          <button type="button" className="p-1.5 rounded-md text-muted-foreground hover:text-destructive">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </li>
      </ul>
      <form className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end pt-2 border-t border-border">
        <FormField name="name" label="Name" value={data.name} onChange={(e) => setData("name", e.target.value)} />
        <FormField name="email" label="Email" type="email" value={data.email} onChange={(e) => setData("email", e.target.value)} />
        <Button><UserPlus className="h-4 w-4" /> Add</Button>
      </form>
    </div>
  );
}

export default function InvitingTeachers() {
  return (
    <>
      <p>
        Once your students are in, the next step in the setup guide is teachers. Each teacher needs to be added to the specific classrooms they teach — just being a teacher at the school isn't enough. You can do them one at a time, or all at once with a spreadsheet for bigger schools.
      </p>
      <Callout variant="note" title="Teachers join once your campaign is approved">
        <p>
          Adding teachers isn't part of the free trial. Once your campaign fee is paid and we've approved the campaign, you can invite them. See <Link href="/help/articles/paying-for-your-campaign">Paying for your campaign</Link>.
        </p>
      </Callout>

      <h2>One at a time</h2>
      <Steps>
        <Step title="Open the classroom">
          <p>From your sidebar, click <strong>Classrooms</strong>, then click the classroom name.</p>
        </Step>
        <Step title="Click Edit classroom and add the teacher">
          <p>You'll see the teacher section right under the classroom name. Type their name and email, click add.</p>
          <Screenshot caption="Adding a teacher to a classroom">
            <AddTeacherInline />
          </Screenshot>
        </Step>
        <Step title="They'll get a sign-in email">
          <p>
            One click and they're in — no password to make. They'll land on their classroom dashboard, ready to log reading minutes.
          </p>
        </Step>
      </Steps>

      <Callout variant="note" title="Already on PagePledge?">
        <p>
          If a teacher already has an account (maybe they teach at a sibling school, or they're a parent here too), the same email works everywhere. They'll just see the new classroom show up next time they sign in.
        </p>
      </Callout>

      <h2>All at once with a spreadsheet</h2>
      <p>
        For a big school, this is faster. In the setup guide's <strong>Invite your teachers</strong> step (or on <strong>Members</strong>), click <strong>Invite teachers from a spreadsheet</strong>, then download the template. We've pre-filled it with one row per classroom — all you need to add is each teacher's name and email next to the right classroom.
      </p>
      <p>
        If the same teacher covers multiple rooms, just add their name in each row. If a classroom doesn't have a teacher yet, leave it blank — we'll skip it. Each teacher gets one welcome email no matter how many classrooms you assigned them to.
      </p>

      <h2>Removing a teacher</h2>
      <p>
        Open the classroom, click <strong>Edit classroom</strong>, click the trash icon by their name. They lose access to that one classroom but stay on as a teacher at your school, so you can assign them somewhere else. To remove them from your school entirely, see <Link href="/help/articles/managing-members">Managing school admins and teachers</Link>.
      </p>

      <h2>What teachers can do</h2>
      <p>
        Teachers can log reading minutes for their classrooms, print take-home sheets, set classroom goals and rewards, and mark prizes as handed out. They can't create campaigns, add or remove students, or see donation details — those stay with you.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/setting-school-milestones">Set up school-wide rewards</Link> — the next step in the setup guide.</li>
        <li><Link href="/help/articles/getting-started-teacher">Getting started as a teacher</Link> — a good first read to send them.</li>
        <li><Link href="/help/articles/managing-members">Managing school admins and teachers</Link>.</li>
      </ul>
    </>
  );
}
